import {
  getFormattedListTimeZones,
  getTimeZoneFromFormattedList,
} from 'app/ProviderSearch/utils'
import Select from 'react-select'
import CustomTooltip from 'common/CustomTooltip'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import { useContext, useMemo } from 'react'
import { minimalTimezoneSet } from 'constants/common'
import { SEARCH_GREY } from 'constants/colors'
import Icon from 'common/Icon'
import { Placement } from 'react-bootstrap/esm/Overlay'
import classNames from 'classnames'

type TimeZonePickerProps = {
  className?: string
  containerClass?: string
  timezoneInfoClass?: string
  tooltipDirection?: Placement
  showClientMsgHint?: boolean
  fontSize?: number
}

const TimeZonePicker: React.FC<TimeZonePickerProps> = ({
  className = '',
  containerClass,
  timezoneInfoClass = 'justify-content-end',
  tooltipDirection = 'bottom',
  showClientMsgHint = true,
  fontSize = 12,
}) => {
  const { setTimeZone, timeZoneObject, setTimeZoneObject } = useContext(
    TimeZoneContext
  )

  const timeZoneSelectItem = useMemo(() => {
    if (timeZoneObject) {
      return getTimeZoneFromFormattedList(
        timeZoneObject.tzName,
        timeZoneObject.tzCode
      )
    }

    return null
  }, [timeZoneObject])

  const onChangeTimeZone = ({ value, tzCode }) => {
    const timeZoneObject = minimalTimezoneSet.find(
      (timeZoneObject) =>
        timeZoneObject.tzName === value && timeZoneObject.tzCode === tzCode
    )
    setTimeZone(timeZoneObject.tzCode)
    setTimeZoneObject(timeZoneObject)
    sessionStorage.setItem(sessionStorage.TIMEZONE_ABBR, timeZoneObject.tzAbbr)
    /** When timezone is set in Provider search page, adding tz abbreviation to session storage.
     ** This key will be used when navigating to provider profile page and will be added as a query param.
     ** On provider profile page, if this key is available, then the timezone in timezone picker will be set as that else practice's lat, lng will be used.
     ** We're doing this because we want the timezone that is set on Provider search page should be shown on Provider profile.
     **/
  }

  const isOptionSelected = (
    option: TimezoneSelectItem,
    selectValue: TimezoneSelectItem[]
  ): boolean => {
    const selectedValue = selectValue[0]
    const doesCodeMatch = option.tzCode === selectedValue?.tzCode
    const doesNameMatch = option.value === selectedValue?.value
    return doesCodeMatch && doesNameMatch
  }

  const timeZoneOptions = useMemo(() => {
    return getFormattedListTimeZones()
  }, [])

  //TODO: Restructure component
  return (
    <div data-testid="timezone-selector" className={containerClass}>
      <Select
        aria-label="timezone selector"
        isOptionSelected={isOptionSelected}
        cacheOptions
        inputId="timezone-picker"
        placeholder="Timezone"
        className={classNames(
          'react-select-container align-self-stretch',
          className
        )}
        classNamePrefix="react-select"
        value={timeZoneSelectItem}
        onChange={onChangeTimeZone}
        options={timeZoneOptions}
        styles={{
          control: (styles) => ({ ...styles, fontSize }),
          option: (styles) => ({ ...styles, fontSize }),
        }}
      />
      {showClientMsgHint && (
        <div className={`d-flex align-items-center ${timezoneInfoClass}`}>
          <div className="fs-12 text-slate-gray mr-1 mt-1">
            Clients will be booked in this timezone
          </div>
          <CustomTooltip
            content="All reminders and communications will reference this timezone. This defaults to the timezone of your search."
            targetElement={
              <Icon
                icon="info"
                fill={SEARCH_GREY}
                className="fa-lg mt-1 c-pointer"
              />
            }
            direction={tooltipDirection}
            hideDelay={250}
          />
        </div>
      )}
    </div>
  )
}

export default TimeZonePicker
